import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Box, Skeleton } from "@mui/material";
import { useAuthContext } from "../../context/AuthContext";
import { getPartnerBillingContext } from "../../api/client";
import { PartnerBillingProvider } from "../../pages/Partner/PartnerConsole/PartnerBillingContext";

function Loading() {
  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Skeleton variant="text" width={260} height={36} />
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
    </Box>
  );
}

/**
 * Área /partner/console — usuário com e-mail cadastrado em reseller_users (API resolve pela sessão).
 * Master é redirecionado ao console da plataforma.
 */
export default function RequirePartnerPortal({ children }) {
  const { userData, bearerToken } = useAuthContext();
  const [status, setStatus] = useState("loading");
  const [billingPayload, setBillingPayload] = useState(null);
  /** Evita desmontar /partner/console (ex.: wizard de novo workspace) a cada refresh de `userData` com o mesmo usuário. */
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    if (!bearerToken) {
      setStatus("noauth");
      return;
    }
    if (userData == null) {
      setStatus("loading");
      return;
    }
    let cancelled = false;
    const userId =
      userData?.id == null || String(userData.id).trim() === "" ? null : String(userData.id).trim();
    const userIdentityChanged =
      lastUserIdRef.current == null || lastUserIdRef.current !== userId;
    if (userIdentityChanged) {
      lastUserIdRef.current = userId;
      setStatus("loading");
    }
    getPartnerBillingContext()
      .then((data) => {
        if (cancelled) return;
        if (Number(userData.master) === 1) {
          setBillingPayload(null);
          setStatus("master");
          return;
        }
        if (userData.scope === "partner" || data?.reseller?.id) {
          setBillingPayload(data ?? null);
          setStatus("ok");
        } else {
          setBillingPayload(null);
          setStatus("denied");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBillingPayload(null);
          setStatus("denied");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [bearerToken, userData]);

  if (!bearerToken || status === "noauth") {
    return <Navigate to="/partner/login" replace />;
  }

  if (userData == null || status === "loading") {
    return <Loading />;
  }

  if (status === "master") {
    return <Navigate to="/platform/xprobase" replace />;
  }

  if (status === "denied") {
    return <Navigate to="/chat" replace />;
  }

  return <PartnerBillingProvider value={billingPayload}>{children}</PartnerBillingProvider>;
}
